// ── Stripe — real payment flow ───────────────────────────────────────────
// Flow:
//   1. Client calls createPaymentIntent() → Edge Function → Stripe API → clientSecret
//   2. Client calls stripe.confirmCardPayment(clientSecret, { payment_method })
//   3. On success → registerForEvent() + save receipt

import { createPaymentIntent, confirmPayment, createSubscription, cancelSubscription as apiCancelSub } from './api';

// ── Load Stripe.js ────────────────────────────────────────────────────────
let _stripe = null;
export async function getStripe() {
  if (_stripe) return _stripe;
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) { console.warn('[Stripe] No publishable key — payment will fail'); return null; }
  // Dynamically load Stripe.js
  if (!window.Stripe) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://js.stripe.com/v3/';
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  _stripe = window.Stripe(key);
  return _stripe;
}

// ── Pay for an event ──────────────────────────────────────────────────────
export async function payForEvent({ event, user, card, cardholderName, token }) {
  try {
    const stripe = await getStripe();
    if (!stripe) return { success: false, error: 'Stripe not configured' };

    const amount = getEventPrice(event, user);
    if (amount === 0) return { success: true, free: true, paymentId: null };

    // 1. Create PaymentIntent on server
    const { clientSecret, error: intentErr } = await createPaymentIntent({
      amount, currency: 'cad', eventId: event.id, userId: user.id,
    }, token);
    if (intentErr) return { success: false, error: intentErr };

    // 2. Confirm payment with card details
    const { paymentIntent, error: confirmErr } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card,
        billing_details: { name: cardholderName, email: user.email },
      },
    });
    if (confirmErr) return { success: false, error: confirmErr.message };
    if (paymentIntent.status !== 'succeeded') return { success: false, error: 'Payment not completed' };

    const receipt = {
      paymentId:   paymentIntent.id,
      eventId:     event.id,
      eventTitle:  event.title,
      amount,
      currency:    'CAD',
      date:        new Date().toISOString(),
      cardLast4:   paymentIntent.payment_method_details?.card?.last4 || '****',
    };

    return { success: true, paymentId: paymentIntent.id, receipt };
  } catch (err) {
    return { success: false, error: err.message || 'Payment failed' };
  }
}

// ── Subscribe to a plan ───────────────────────────────────────────────────
export async function subscribeToPlan({ priceId, userId }, token) {
  try {
    const result = await createSubscription({ priceId, userId }, token);
    return { success: true, ...result };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

// ── Cancel subscription ───────────────────────────────────────────────────
export async function cancelSubscription({ subscriptionId }, token) {
  try {
    await apiCancelSub({ subscriptionId }, token);
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

// ── Receipt storage — Supabase (not localStorage) ─────────────────────────
// Receipts are stored in the payments table. Use db.payments() to read them.
export async function getReceipts(userId) {
  const { db } = await import('./supabase');
  const { data } = await db.payments()
    ?.select('*')
    .eq('user_id', userId)
    .eq('status', 'succeeded')
    .order('created_at', { ascending: false })
    .limit(20);
  return data || [];
}

// ── Price helper (same as in events.js for convenience) ───────────────────
export function getEventPrice(event, user) {
  const plan = user?.plan || 'free';
  if (Array.isArray(event.pricing)) {
    const row = event.pricing.find(p => p.plan === plan);
    return row ? row.price : (event.pricing.find(p => p.plan === 'free')?.price ?? 0);
  }
  if (plan === 'royal' || plan === 'founder') return event.price_royal ?? event.priceRoyal ?? event.price_gold ?? event.priceGold ?? event.price ?? 0;
  if (plan === 'honey') return event.price_honey ?? event.priceHoney ?? event.price ?? 0;
  return event.price ?? 0;
}

// ── Refund ────────────────────────────────────────────────────────────────
export async function refundPayment({ paymentIntentId, amount }, token) {
  try {
    const { refundId } = await import('./api').then(m => m.refundPayment({ paymentIntentId, amount }, token));
    return { success: true, refundId };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export function isRefundEligible(event, registration) {
  // Use event date — refund window is hours BEFORE the event, not after purchase
  if (!event?.date) return false;
  const eventDate  = new Date(event.date + 'T' + (event.time || '00:00'));
  const hoursLeft  = (eventDate - Date.now()) / 3600000;
  const deadline   = event.refund_deadline_h ?? 48;
  return hoursLeft > deadline;
}

export function formatCardNumber(v) {
  return v.replace(/\D/g,'').slice(0,16).replace(/(\d{4})/g,'$1 ').trim();
}
export function formatExpiry(v) {
  return v.replace(/\D/g,'').slice(0,4).replace(/(\d{2})(\d)/,'$1/$2');
}
export function formatCVC(v) { return v.replace(/\D/g,'').slice(0,4); }
